/**
 * useObjectUrl — Blob object URL for a File preview, revoked automatically
 * when the file changes or the component unmounts.
 *
 * Never call `URL.createObjectURL` inline in JSX: that allocates a fresh
 * blob URL on every render and never revokes any of them, leaking memory
 * for as long as the page lives. Creating the URL inside the effect and
 * mirroring it into state keeps a single owned allocation with a paired
 * revoke.
 *
 * Returns `null` until the effect runs (and when `file` is nullish) —
 * guard rendering on it, since `<img src="">` resolves to the page URL
 * and fires a spurious request.
 */

import { useEffect, useState } from 'react'

export function useObjectUrl(file: File | Blob | null | undefined): string | null {
  const [url, setUrl] = useState<string | null>(null)
  useEffect(() => {
    if (!file) {
      setUrl(null)
      return
    }
    const u = URL.createObjectURL(file)
    setUrl(u)
    return () => URL.revokeObjectURL(u)
  }, [file])
  return url
}
